import { create } from 'zustand';
import type { LoadingStoreConnectionProtocol } from '../types/connection';

// 연결 정보
interface ConnectionInfo {
  type: LoadingStoreConnectionProtocol;
  isConnected: boolean;
  lastConnected?: Date;
  port?: string; // Serial 포트명
  deviceId?: string; // BLE 디바이스 ID
}

// 기기 정보 타입
interface DeviceInfo {
  id: string;
  name: string;
  serialNumber?: string;
  connections: ConnectionInfo[];
  deviceData?: { serial_number?: string; [key: string]: unknown };
}

interface LoadingState {
  isLoading: boolean;
  message: string;
  progress?: number; // 0-100, undefined = indeterminate
  showCancelButton: boolean;
  onCancel: (() => void) | null;
  showLoading: (message?: string, showCancelButton?: boolean, onCancel?: () => void) => void;
  hideLoading: () => void;
  setProgress: (progress: number | undefined) => void;
}

interface DeviceState {
  connectedDevices: DeviceInfo[];
  addDevice: (device: DeviceInfo) => void;
  removeDevice: (deviceId: string) => void;
  updateDevice: (deviceId: string, updates: Partial<DeviceInfo>) => void;
  addConnection: (deviceId: string, connection: ConnectionInfo) => void;
  removeConnection: (deviceId: string, connectionType: LoadingStoreConnectionProtocol) => void;
  getConnectedDevice: (type: LoadingStoreConnectionProtocol) => DeviceInfo | null;
  getDeviceById: (deviceId: string) => DeviceInfo | null;
  getAnyConnectedDevice: () => DeviceInfo | null;
  getDeviceBySerialNumber: (serialNumber: string) => DeviceInfo | null;
  updateConnectedDevice: (
    serialNumber: string,
    connectionType: LoadingStoreConnectionProtocol,
    deviceInfo?: { name?: string; serialNumber?: string; serial_number?: string; [key: string]: unknown }
  ) => void;
  disconnectDevice: (serialNumber: string, connectionType: LoadingStoreConnectionProtocol) => void;
  clearDevices: () => void;
}

type AppStore = LoadingState & DeviceState;

export const useAppStore = create<AppStore>((set, get) => ({
  // Loading State
  isLoading: false,
  message: '로딩 중...',
  progress: undefined,
  showCancelButton: false,
  onCancel: null,
  showLoading: (message = '로딩 중...', showCancelButton = false, onCancel = undefined) => {
    set({ isLoading: true, message, showCancelButton, onCancel: onCancel ?? null, progress: undefined });
  },
  hideLoading: () => {
    set({ isLoading: false, message: '로딩 중...', showCancelButton: false, onCancel: null, progress: undefined });
  },
  setProgress: (progress) => {
    set({ progress });
  },

  // Device State
  connectedDevices: [],
  addDevice: device => {
    // ZUSTAND: 디바이스 추가
    set(state => ({
      connectedDevices: [...state.connectedDevices.filter(d => d.id !== device.id), device],
    }));
  },
  removeDevice: deviceId => {
    // ZUSTAND: 디바이스 제거
    set(state => ({
      connectedDevices: state.connectedDevices.filter(d => d.id !== deviceId),
    }));
  },
  updateDevice: (deviceId, updates) => {
    // ZUSTAND: 디바이스 정보 업데이트
    set(state => ({
      connectedDevices: state.connectedDevices.map(d =>
        d.id === deviceId ? { ...d, ...updates } : d
      ),
    }));
  },
  addConnection: (deviceId, connection) =>
    set(state => ({
      connectedDevices: state.connectedDevices.map(d =>
        d.id === deviceId
          ? {
              ...d,
              connections: [...d.connections.filter(c => c.type !== connection.type), connection],
            }
          : d
      ),
    })),
  removeConnection: (deviceId, connectionType) =>
    set(state => ({
      connectedDevices: state.connectedDevices.map(d =>
        d.id === deviceId
          ? {
              ...d,
              connections: d.connections.filter(c => c.type !== connectionType),
            }
          : d
      ),
    })),
  getConnectedDevice: type => {
    const state = get();
    const dummyDeviceIds = ['mini-device-1', 'bell-device-1', 'lite-device-1'];

    return (
      state.connectedDevices.find(
        d =>
          d.connections.some(c => c.type === type && c.isConnected) &&
          !dummyDeviceIds.includes(d.id) &&
          !dummyDeviceIds.includes(d.serialNumber || '')
      ) || null
    );
  },
  // 현재 연결된 모든 기기 조회 (serial 또는 ble)
  // 더미 기기 필터링 (mini-device-1, bell-device-1 등)
  // 가장 최근에 연결된 기기 반환
  getAnyConnectedDevice: () => {
    const state = get();
    const dummyDeviceIds = ['mini-device-1', 'bell-device-1', 'lite-device-1'];

    // 연결된 기기 필터링 (더미 기기 제외)
    const connectedDevices = state.connectedDevices.filter(
      d =>
        d.connections.some(c => c.isConnected) &&
        !dummyDeviceIds.includes(d.id) &&
        !dummyDeviceIds.includes(d.serialNumber || '')
    );

    if (connectedDevices.length === 0) {
      return null;
    }

    // 가장 최근에 연결된 기기 반환 (lastConnected 기준)
    return connectedDevices.reduce((latest, current) => {
      const latestConnection = latest.connections.find(c => c.isConnected);
      const currentConnection = current.connections.find(c => c.isConnected);

      const latestTime = latestConnection?.lastConnected?.getTime() || 0;
      const currentTime = currentConnection?.lastConnected?.getTime() || 0;

      return currentTime > latestTime ? current : latest;
    });
  },
  getDeviceById: deviceId => {
    const state = get();
    return state.connectedDevices.find(d => d.id === deviceId) || null;
  },
  getDeviceBySerialNumber: serialNumber => {
    const state = get();
    return state.connectedDevices.find(d => d.serialNumber === serialNumber) || null;
  },
  updateConnectedDevice: (serialNumber, connectionType, deviceInfo) => {
    // ZUSTAND: 연결된 디바이스 정보 업데이트
    const state = get();

    // 더미 기기 제거 (mini-device-1, bell-device-1, lite-device-1 등)
    const dummyDeviceIds = ['mini-device-1', 'bell-device-1', 'lite-device-1'];
    const cleanedDevices = state.connectedDevices.filter(
      d => !dummyDeviceIds.includes(d.id) && !dummyDeviceIds.includes(d.serialNumber || '')
    );

    const existingDevice = cleanedDevices.find(d => d.serialNumber === serialNumber);

    // deviceInfo에서 serial_number 또는 serialNumber 추출
    const finalSerialNumber = deviceInfo?.serialNumber || deviceInfo?.serial_number || serialNumber;

    if (existingDevice) {
      set({
        connectedDevices: cleanedDevices.map(d =>
          d.serialNumber === serialNumber || d.serialNumber === finalSerialNumber
            ? {
                ...d,
                serialNumber: finalSerialNumber,
                name: deviceInfo?.name || d.name,
                connections: [
                  ...d.connections.filter(c => c.type !== connectionType),
                  {
                    type: connectionType,
                    isConnected: true,
                    lastConnected: new Date(),
                  },
                ],
                deviceData: deviceInfo || d.deviceData,
              }
            : d
        ),
      });
    } else {
      set({
        connectedDevices: [
          ...cleanedDevices,
          {
            id: finalSerialNumber,
            name: deviceInfo?.name || finalSerialNumber,
            serialNumber: finalSerialNumber,
            connections: [
              {
                type: connectionType,
                isConnected: true,
                lastConnected: new Date(),
              },
            ],
            deviceData: deviceInfo,
          },
        ],
      });
    }
  },
  disconnectDevice: (serialNumber, connectionType) => {
    // ZUSTAND: 디바이스 연결 해제
    const state = get();
    set({
      connectedDevices: state.connectedDevices.map(d =>
        d.serialNumber === serialNumber
          ? {
              ...d,
              connections: d.connections.map(c =>
                c.type === connectionType ? { ...c, isConnected: false } : c
              ),
            }
          : d
      ),
    });
  },
  clearDevices: () => {
    // ZUSTAND: 모든 디바이스 제거
    set({ connectedDevices: [] });
  },
}));


// 개발 환경에서 브라우저 콘솔에서 전역 상태 확인 가능하도록 설정
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).__BELLCLOUD_APP_STORE__ = useAppStore;
}
